/**
 * PR45: MCP Connection Registry — 10 connections, all disabled, no real connections
 * @version 0.1.12
 * @safety enabled=false, realConnectionEnabled=false, networkAllowed=false,
 *         productionDbAllowed=false, fileMutationAllowed=false for all entries
 */
export const MCP_CONNECTION_REGISTRY = [
    {
        id: "supabase-db",
        name: "Supabase Database",
        type: "database",
        version: "0.1.0",
        description: "Preview-only Supabase DB connection — no real DB calls",
        enabled: false, realConnectionEnabled: false, networkAllowed: false,
        productionDbAllowed: false, fileMutationAllowed: false, dryRunOnly: true,
        requireOwnerApproval: true, auditEnabled: true, optional: true,
    },
    {
        id: "supabase-vector",
        name: "Supabase Vector Store",
        type: "vector-store",
        version: "0.1.0",
        description: "Preview-only Supabase vector store — no RAG/vector mutation",
        enabled: false, realConnectionEnabled: false, networkAllowed: false,
        productionDbAllowed: false, fileMutationAllowed: false, dryRunOnly: true,
        requireOwnerApproval: true, auditEnabled: true, optional: true,
    },
    {
        id: "vercel-api",
        name: "Vercel API Gateway",
        type: "api-gateway",
        version: "0.1.0",
        description: "Preview-only Vercel API gateway — no real deployment calls",
        enabled: false, realConnectionEnabled: false, networkAllowed: false,
        productionDbAllowed: false, fileMutationAllowed: false, dryRunOnly: true,
        requireOwnerApproval: true, auditEnabled: true, optional: true,
    },
    {
        id: "local-file-system",
        name: "Local File System",
        type: "file-system",
        version: "0.1.0",
        description: "Preview-only local file system connection — no real file operations",
        enabled: false, realConnectionEnabled: false, networkAllowed: false,
        productionDbAllowed: false, fileMutationAllowed: false, dryRunOnly: true,
        requireOwnerApproval: true, auditEnabled: true, optional: true,
    },
    {
        id: "redis-cache",
        name: "Redis Cache",
        type: "cache",
        version: "0.1.0",
        description: "Preview-only Redis cache connection — no real cache operations",
        enabled: false, realConnectionEnabled: false, networkAllowed: false,
        productionDbAllowed: false, fileMutationAllowed: false, dryRunOnly: true,
        requireOwnerApproval: true, auditEnabled: true, optional: true,
    },
    {
        id: "elasticsearch",
        name: "Elasticsearch",
        type: "search",
        version: "0.1.0",
        description: "Preview-only Elasticsearch connection — no real search calls",
        enabled: false, realConnectionEnabled: false, networkAllowed: false,
        productionDbAllowed: false, fileMutationAllowed: false, dryRunOnly: true,
        requireOwnerApproval: true, auditEnabled: true, optional: true,
    },
    {
        id: "rabbitmq",
        name: "RabbitMQ Message Queue",
        type: "message-queue",
        version: "0.1.0",
        description: "Preview-only RabbitMQ connection — no real message publishing",
        enabled: false, realConnectionEnabled: false, networkAllowed: false,
        productionDbAllowed: false, fileMutationAllowed: false, dryRunOnly: true,
        requireOwnerApproval: true, auditEnabled: true, optional: true,
    },
    {
        id: "oauth-provider",
        name: "OAuth Provider",
        type: "auth",
        version: "0.1.0",
        description: "Preview-only OAuth provider connection — no real auth calls",
        enabled: false, realConnectionEnabled: false, networkAllowed: false,
        productionDbAllowed: false, fileMutationAllowed: false, dryRunOnly: true,
        requireOwnerApproval: true, auditEnabled: true, optional: true,
    },
    {
        id: "s3-storage",
        name: "S3 Storage",
        type: "storage",
        version: "0.1.0",
        description: "Preview-only S3 storage connection — no real file uploads",
        enabled: false, realConnectionEnabled: false, networkAllowed: false,
        productionDbAllowed: false, fileMutationAllowed: false, dryRunOnly: true,
        requireOwnerApproval: true, auditEnabled: true, optional: true,
    },
    {
        id: "general-mcp",
        name: "General MCP Connection",
        type: "general",
        version: "0.1.0",
        description: "Preview-only general MCP connection — no real provider calls",
        enabled: false, realConnectionEnabled: false, networkAllowed: false,
        productionDbAllowed: false, fileMutationAllowed: false, dryRunOnly: true,
        requireOwnerApproval: true, auditEnabled: true, optional: true,
    },
];
export function getMcpConnectionById(id) {
    return MCP_CONNECTION_REGISTRY.find(c => c.id === id);
}
export function listMcpConnectionIds() {
    return MCP_CONNECTION_REGISTRY.map(c => c.id);
}
export function getOptionalConnections() {
    return MCP_CONNECTION_REGISTRY.filter(c => c.optional === true);
}
export function assertNoLiveConnections(entries = MCP_CONNECTION_REGISTRY) {
    for (const conn of entries) {
        if (conn.enabled !== false)
            throw new Error(`SAFETY VIOLATION: Connection ${conn.id} has enabled=true`);
        if (conn.realConnectionEnabled !== false)
            throw new Error(`SAFETY VIOLATION: Connection ${conn.id} has realConnectionEnabled=true`);
        if (conn.networkAllowed !== false)
            throw new Error(`SAFETY VIOLATION: Connection ${conn.id} has networkAllowed=true`);
        if (conn.productionDbAllowed !== false)
            throw new Error(`SAFETY VIOLATION: Connection ${conn.id} has productionDbAllowed=true`);
        if (conn.fileMutationAllowed !== false)
            throw new Error(`SAFETY VIOLATION: Connection ${conn.id} has fileMutationAllowed=true`);
    }
}
export function validateMcpConnectionRegistry(entries) {
    const list = entries ?? MCP_CONNECTION_REGISTRY;
    const errors = [];
    const ids = new Set();
    for (const conn of list) {
        if (ids.has(conn.id))
            errors.push(`Duplicate connection id: ${conn.id}`);
        ids.add(conn.id);
        if (!conn.id)
            errors.push("Connection missing id");
        if (!conn.name)
            errors.push(`Connection ${conn.id} missing name`);
        if (!conn.type)
            errors.push(`Connection ${conn.id} missing type`);
        if (!conn.version)
            errors.push(`Connection ${conn.id} missing version`);
        if (!conn.description)
            errors.push(`Connection ${conn.id} missing description`);
        if (conn.enabled !== false)
            errors.push(`Connection ${conn.id}: enabled must be false`);
        if (conn.realConnectionEnabled !== false)
            errors.push(`Connection ${conn.id}: realConnectionEnabled must be false`);
        if (conn.networkAllowed !== false)
            errors.push(`Connection ${conn.id}: networkAllowed must be false`);
        if (conn.productionDbAllowed !== false)
            errors.push(`Connection ${conn.id}: productionDbAllowed must be false`);
        if (conn.fileMutationAllowed !== false)
            errors.push(`Connection ${conn.id}: fileMutationAllowed must be false`);
    }
    return { valid: errors.length === 0, errors };
}
//# sourceMappingURL=registry.js.map